import React, { useCallback, useEffect, useState } from 'react'
import whatsappApi from './whatsappApi'
import myToast from '../../Utils/MyToast'

const WA = '#25D366'
const WA_DARK = '#128C7E'

const STATUS_STYLES = {
  pending: { bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
  processing: { bg: '#eff6ff', color: '#1d4ed8', border: '#93c5fd' },
  sent: { bg: '#ecfdf5', color: WA_DARK, border: `${WA}66` },
  delivered: { bg: '#ecfdf5', color: WA_DARK, border: `${WA}66` },
  read: { bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
  failed: { bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5' },
}

// Created By Cursor — show filled preview when message stores { preview, body_params }
function formatQueueMessage(raw) {
  const text = String(raw || '').trim()
  if (!text) return '—'
  try {
    const parsed = JSON.parse(text)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      if (parsed.preview) return String(parsed.preview)
      if (Array.isArray(parsed.body_params)) {
        return parsed.body_params.join(' · ')
      }
    }
    if (Array.isArray(parsed)) return parsed.join(' · ')
  } catch (e) {
    /* plain text */
  }
  return text
}

// Created By Cursor
function formatWhen(raw) {
  if (!raw) return '—'
  try {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return String(raw)
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (e) {
    return String(raw)
  }
}

// Created By Cursor
function StatusChip({ status }) {
  const key = String(status || '').toLowerCase()
  const style = STATUS_STYLES[key] || {
    bg: '#f1f5f9',
    color: '#475569',
    border: '#cbd5e1',
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 800,
        textTransform: 'capitalize',
        color: style.color,
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: 999,
        padding: '3px 9px',
      }}
    >
      {status || '—'}
    </span>
  )
}

// Created By Cursor
const WhatsAppQueuePanel = () => {
  const [group, setGroup] = useState('pending')
  const [items, setItems] = useState([])
  const [counts, setCounts] = useState({ pending: 0, sent: 0, failed: 0 })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const res = await whatsappApi.getQueue(group, 200, 0)
      if (res.api_status === 'OK') {
        const data = res.data || {}
        setItems(Array.isArray(data.items) ? data.items : [])
        setCounts(data.counts || { pending: 0, sent: 0, failed: 0 })
      } else {
        const msg = res.api_remark || 'Failed to load queue'
        setLoadError(msg)
        setItems([])
        myToast.notify_error(msg)
      }
    } catch (e) {
      const msg = e?.message || 'Failed to load queue'
      setLoadError(msg)
      setItems([])
      myToast.notify_error(msg)
    } finally {
      setLoading(false)
    }
  }, [group])

  useEffect(() => {
    load()
  }, [load])

  const tabs = [
    { key: 'pending', label: 'Pending', count: counts.pending, color: '#c2410c' },
    { key: 'sent', label: 'Sent', count: counts.sent, color: WA_DARK },
    { key: 'failed', label: 'Failed', count: counts.failed, color: '#b91c1c' },
  ]

  return (
    <div style={{ minHeight: 200 }}>
      <div
        className="d-flex flex-wrap align-items-center justify-content-between"
        style={{ gap: 10, marginBottom: 14 }}
      >
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>
            Outbound message queue
          </div>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 2 }}>
            Pending, sent, and failed WhatsApp messages for this school
          </div>
        </div>
        <button type="button" onClick={load} disabled={loading} style={btnGhost}>
          <i className="fa fa-refresh" style={{ marginRight: 6 }} />
          Refresh
        </button>
      </div>

      <div
        style={{
          border: `1px solid ${WA}33`,
          borderRadius: 14,
          overflow: 'hidden',
          background: '#fff',
          boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '10px 12px',
            background: 'linear-gradient(180deg, #f0fdf4, #f8fafc)',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {tabs.map((t) => {
              const active = group === t.key
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setGroup(t.key)}
                  style={{
                    border: active ? `1.5px solid ${WA}` : '1px solid #e2e8f0',
                    background: active ? `${WA}18` : '#fff',
                    color: active ? WA_DARK : '#475569',
                    fontWeight: 800,
                    fontSize: 12,
                    padding: '7px 12px',
                    borderRadius: 999,
                    cursor: 'pointer',
                  }}
                >
                  {t.label}
                  <span
                    style={{
                      marginLeft: 6,
                      background: active ? WA : '#e2e8f0',
                      color: active ? '#fff' : '#475569',
                      borderRadius: 999,
                      padding: '1px 7px',
                      fontSize: 11,
                    }}
                  >
                    {t.count || 0}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {loading && (
          <div
            style={{
              height: 3,
              background: `linear-gradient(90deg, ${WA}, transparent)`,
            }}
          />
        )}

        {loading ? (
          <div style={{ padding: 28, color: '#94a3b8', fontWeight: 600 }}>
            Loading queue…
          </div>
        ) : loadError ? (
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            <i className="fa fa-exclamation-circle" style={{ fontSize: 28, color: '#b91c1c', marginBottom: 8 }} />
            <div style={{ fontWeight: 800, fontSize: 14, color: '#991b1b' }}>
              Could not load queue
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 6, maxWidth: 420, margin: '6px auto 0' }}>
              {loadError}
            </div>
            <button type="button" onClick={load} style={{ ...btnGhost, marginTop: 12 }}>
              Try again
            </button>
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8' }}>
            <i className="fa fa-inbox" style={{ fontSize: 28, marginBottom: 8 }} />
            <div style={{ fontWeight: 800, fontSize: 14, color: '#64748b' }}>
              No {group} messages
            </div>
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 320px)' }}>
            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  background: '#f8fafc',
                }}
              >
                <tr>
                  <th style={th}>Contact</th>
                  <th style={th}>Student</th>
                  <th style={th}>Template</th>
                  <th style={{ ...th, minWidth: 220 }}>Message</th>
                  <th style={th}>Status</th>
                  <th style={th}>Created</th>
                  <th style={th}>Sent</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={td}>
                      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {row.contact_number}
                      </span>
                    </td>
                    <td style={{ ...td, color: '#64748b' }}>
                      {row.recipient_student_id || '—'}
                    </td>
                    <td style={td}>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: WA_DARK,
                          background: `${WA}14`,
                          borderRadius: 6,
                          padding: '3px 7px',
                        }}
                      >
                        {row.template_name || row.message_type || '—'}
                      </span>
                    </td>
                    <td
                      style={{
                        ...td,
                        maxWidth: 340,
                        color: '#334155',
                        fontSize: 12,
                        lineHeight: 1.45,
                      }}
                    >
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {formatQueueMessage(row.message)}
                      </div>
                    </td>
                    <td style={td}>
                      <StatusChip status={row.status} />
                      {row.error_message ? (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#b91c1c',
                            marginTop: 4,
                            maxWidth: 160,
                          }}
                        >
                          {row.error_message}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#64748b' }}>
                      {formatWhen(row.created_at)}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#64748b' }}>
                      {formatWhen(row.sent_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

const th = {
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#64748b',
  borderBottom: `2px solid ${WA}33`,
  whiteSpace: 'nowrap',
}

const td = {
  padding: '10px 12px',
  verticalAlign: 'top',
}

const btnGhost = {
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#475569',
  fontWeight: 700,
  fontSize: 12,
  padding: '6px 12px',
  borderRadius: 8,
  cursor: 'pointer',
}

export default WhatsAppQueuePanel
